import React, { useEffect, useState, useCallback } from 'react';
import { Target, Copy, Check, Zap, Pencil, TrendingUp, Rocket } from 'lucide-react';
import { HelpTooltip } from './HelpTooltip';
import { Link } from 'react-router-dom';
import { PerformanceSection } from './PerformanceSection';
import {
  getCampaignTracking, deployCampaignTracking, setManualBookings,
  updateDeploymentSettings, simulateProviderWebhook,
} from '../utils/api';

const PROVIDER_NAMES = { servicetitan: 'ServiceTitan', jobber: 'Jobber', housecallpro: 'Housecall Pro' };
const bigInputCls = 'w-full bg-black border border-divider rounded-lg px-3 py-2.5 text-white text-lg font-bold focus:border-lime focus:outline-none';

export const CampaignKPICard = ({ campaignId, goalData }) => {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState('');
  const [price, setPrice] = useState('');
  const [spend, setSpend] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await getCampaignTracking(campaignId);
      setData(res.data);
      const d = res.data.deployment;
      if (d) {
        setJobs(d.booking_count ? String(d.booking_count) : '');
        setPrice(d.avg_job_value ? String(d.avg_job_value) : (res.data.avg_job_value ? String(res.data.avg_job_value) : ''));
        setSpend(d.campaign_cost ? String(d.campaign_cost) : '');
      }
    } catch (e) {
      console.error('tracking fetch failed', e);
    }
  }, [campaignId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const handleDeploy = async () => {
    setBusy(true);
    try {
      await deployCampaignTracking(campaignId);
      await fetchData();
      flash('Tracking started — use your tracking code in ads & booking forms');
    } catch (e) {
      flash(e.response?.data?.detail || 'Deploy failed');
    } finally { setBusy(false); }
  };

  const savePrice = () => {
    const v = parseFloat(price);
    if (isNaN(v) || v < 0) return;
    updateDeploymentSettings(dep.id, { avg_job_value: v }).then(() => flash('Job price saved')).catch(() => flash('Save failed'));
  };

  const saveSpend = () => {
    const v = parseFloat(spend);
    if (isNaN(v) || v < 0) return;
    updateDeploymentSettings(dep.id, { campaign_cost: v }).then(() => flash('Ad spend saved')).catch(() => flash('Save failed'));
  };

  const saveJobs = () => {
    const n = parseInt(jobs, 10);
    if (isNaN(n) || n < 0 || n === (dep.booking_count || 0)) return;
    setManualBookings(dep.id, { jobs_booked: n }).then(() => flash('Jobs booked saved')).catch(() => flash('Save failed'));
  };

  const handleSimulate = async () => {
    setBusy(true);
    try {
      await simulateProviderWebhook('servicetitan', { tracking_id: dep.tracking_id, amount: 385 });
      await fetchData();
      flash('Sandbox booking event received & auto-matched ✓');
    } catch (e) {
      flash(e.response?.data?.detail || 'Simulation failed');
    } finally { setBusy(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(dep.tracking_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!data) return null;
  const dep = data.deployment;
  const hasConnection = (data.connections || []).some((c) => c.status === 'connected');
  const daysSinceDeploy = dep ? Math.floor((Date.now() - new Date(dep.deploy_date).getTime()) / 86400000) : 0;
  const showNudge = dep && daysSinceDeploy >= 30 && (dep.booking_count || 0) === 0;

  // live math from the input fields
  const nJobs = parseInt(jobs, 10) || 0;
  const nPrice = parseFloat(price) || 0;
  const nSpend = parseFloat(spend) || 0;
  const revenue = nJobs * nPrice;
  const nothingEntered = !jobs && !price && !spend;

  return (
    <div className="bg-card-bg border border-divider rounded-xl p-6 mb-8" data-testid="campaign-kpi-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-white font-bold text-xl flex items-center gap-2">
          <Target size={18} className="text-lime" /> Campaign Performance
          <HelpTooltip text="Your money math for this campaign. Enter your real job price and ad spend, and we show your actual return — no marketing degree required." testId="help-kpi-card" />
        </h3>
        {dep && dep.booking_source === 'auto' && (
          <span data-testid="kpi-badge-auto" className="px-3 py-1 rounded-full text-xs font-bold bg-lime/15 text-lime border border-lime/40 flex items-center gap-1.5">
            <Zap size={12} /> Auto-synced from {PROVIDER_NAMES[dep.booking_provider] || dep.booking_provider}
          </span>
        )}
        {dep && dep.booking_source === 'manual' && (
          <span data-testid="kpi-badge-manual" className="px-3 py-1 rounded-full text-xs font-bold bg-orange/15 text-orange border border-orange/40 flex items-center gap-1.5">
            <Pencil size={12} /> Manually entered
          </span>
        )}
      </div>

      {goalData && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="kpi-goal-watch">
          <span className="text-xs text-gray font-semibold uppercase tracking-wide">
            Numbers to watch for “{goalData.label}”:
          </span>
          {(goalData.kpi_watch || []).map((k) => (
            <span key={k} className="px-2.5 py-1 rounded-full bg-orange/10 border border-orange/30 text-orange text-xs font-semibold">{k}</span>
          ))}
        </div>
      )}

      {!dep ? (
        <div className="flex items-center justify-between flex-wrap gap-4">
          <p className="text-light-gray text-sm">
            Start tracking to get a unique code for this campaign — bookings sync automatically from your booking system, or enter them manually.
          </p>
          <button
            data-testid="kpi-deploy-btn"
            onClick={handleDeploy}
            disabled={busy}
            className="px-5 py-2.5 rounded-lg bg-lime text-black font-bold text-sm hover:bg-lime/90 disabled:opacity-50"
          >
            {busy ? 'Starting…' : 'Deploy & Start Tracking'}
          </button>
        </div>
      ) : (
        <>
          {showNudge && (
            <div className="mb-4 p-3 rounded-lg bg-orange/10 border border-orange/40 text-orange text-sm" data-testid="kpi-30day-nudge">
              📣 It's been {daysSinceDeploy} days since you deployed this campaign — how many jobs did it book? Enter it below.
            </div>
          )}

          {/* Summary tiles — display only, mirror the inputs below */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <div className="bg-black rounded-lg border border-divider p-4">
              <p className="text-2xl font-black text-white" data-testid="kpi-booking-count">{jobs ? nJobs : '—'}</p>
              <p className="text-xs text-gray mt-1">Jobs Booked</p>
            </div>
            <div className="bg-black rounded-lg border border-divider p-4">
              <p className="text-2xl font-black text-lime flex items-center gap-1" data-testid="kpi-revenue-estimate">
                <TrendingUp size={18} />{nPrice > 0 && jobs ? `$${revenue.toLocaleString()}` : '—'}
              </p>
              <p className="text-xs text-gray mt-1">Revenue Estimate</p>
            </div>
            <div className="bg-black rounded-lg border border-divider p-4">
              <p className="text-2xl font-black text-white" data-testid="kpi-price-tile">{nPrice > 0 ? `$${nPrice.toLocaleString()}` : '—'}</p>
              <p className="text-xs text-gray mt-1">What You Charge</p>
            </div>
            <div className="bg-black rounded-lg border border-divider p-4">
              <div className="flex items-center gap-2">
                <code className="text-sm font-bold text-orange" data-testid="kpi-tracking-id">{dep.tracking_id}</code>
                <button onClick={copyCode} data-testid="kpi-copy-tracking" className="text-gray hover:text-white">
                  {copied ? <Check size={14} className="text-lime" /> : <Copy size={14} />}
                </button>
              </div>
              <p className="text-xs text-gray mt-1">Tracking Code (use in UTMs / booking notes)</p>
            </div>
          </div>

          {/* Grouped inputs — the ONE place to enter your numbers */}
          <div className="p-5 rounded-lg bg-black border border-lime/30 mb-4" data-testid="kpi-inputs-group">
            <p className="text-white font-bold text-lg mb-4 flex items-center gap-2">
              <TrendingUp size={16} className="text-lime" /> Enter Your Numbers
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-light-gray font-semibold mb-1.5">What do you charge per job for this service? <HelpTooltip size={13} text="Your average price for this type of job. We multiply it by jobs booked to show your real revenue — YOUR numbers, not industry averages." testId="help-kpi-price" /></label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray font-bold">$</span>
                  <input
                    data-testid="kpi-price-input"
                    type="number" min="0" placeholder="450"
                    className={`${bigInputCls} pl-7`}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    onBlur={savePrice}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-light-gray font-semibold mb-1.5">What did you spend on ads for this campaign? <HelpTooltip size={13} text="The total you've paid Google/Meta for this campaign so far. Find it in your ad account's billing section — takes 30 seconds." testId="help-kpi-spend" /></label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray font-bold">$</span>
                  <input
                    data-testid="kpi-adspend-input"
                    type="number" min="0" placeholder="500"
                    className={`${bigInputCls} pl-7`}
                    value={spend}
                    onChange={(e) => setSpend(e.target.value)}
                    onBlur={saveSpend}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-light-gray font-semibold mb-1.5">Jobs booked (last 30 days) <HelpTooltip size={13} text="How many actual jobs this campaign booked. If your booking system (ServiceTitan, Jobber, Housecall Pro) is connected, this fills in automatically." testId="help-kpi-jobs" /></label>
                <input
                  data-testid="kpi-manual-input"
                  type="number" min="0" placeholder="0"
                  className={bigInputCls}
                  value={jobs}
                  onChange={(e) => setJobs(e.target.value)}
                  onBlur={saveJobs}
                />
              </div>
            </div>

            {/* Live result line */}
            <div className="mt-4 pt-4 border-t border-divider" data-testid="kpi-roi-calculator">
              {nothingEntered ? (
                <p className="text-light-gray text-sm flex items-center gap-2" data-testid="kpi-empty-state">
                  <Rocket size={15} className="text-orange" /> Run this campaign and enter your numbers above to see your ROI.
                </p>
              ) : nPrice <= 0 ? (
                <p className="text-orange text-sm font-semibold" data-testid="kpi-price-hint">
                  ⬆ Enter your job price above to calculate ROI.
                </p>
              ) : (
                <p className="text-sm flex items-center gap-2 flex-wrap">
                  <span className="text-white font-bold">{nJobs} jobs</span>
                  <span className="text-light-gray">× ${nPrice.toLocaleString()} =</span>
                  <span className="text-lime font-black text-2xl" data-testid="kpi-roi-value">${revenue.toLocaleString()}</span>
                  {nSpend > 0 && (
                    <>
                      <span className="text-light-gray">on ${nSpend.toLocaleString()} ad spend =</span>
                      <span className="font-black text-2xl text-orange" data-testid="kpi-roas-value">
                        {(revenue / nSpend).toFixed(1)}× return
                      </span>
                    </>
                  )}
                  {nSpend <= 0 && <span className="text-gray text-xs">(add your ad spend to see your return multiple)</span>}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-3">
            <p className="text-xs text-gray">Values save automatically as you enter them.</p>
            {hasConnection ? (
              <button
                data-testid="kpi-simulate-btn"
                onClick={handleSimulate}
                disabled={busy}
                className="ml-auto px-4 py-2 rounded-lg border border-divider text-gray-light text-xs hover:text-white disabled:opacity-50"
              >
                ⚡ Test auto-sync (sandbox)
              </button>
            ) : (
              <Link to="/integrations" data-testid="kpi-connect-link" className="ml-auto text-xs text-orange hover:underline">
                Connect your booking system for automatic tracking →
              </Link>
            )}
          </div>
        </>
      )}
      {msg && <p className="text-xs text-lime mt-3" data-testid="kpi-message">{msg}</p>}
      <PerformanceSection campaignId={campaignId} goalData={goalData} />
    </div>
  );
};
